import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdReviewsOptions, getEntitiesByFpIdReviewsQueryKey, postEntitiesByFpIdActionsMutation } from '@onefootprint/fetch-dashboard';
import { useIntl, useRequestErrorToast } from '@onefootprint/hooks';
import { IcoWarning16 } from '@onefootprint/icons';
import type { Review } from '@onefootprint/request-types/dashboard';
import { Divider, LinkButton, useConfirmationDialog, useToast } from '@onefootprint/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, easeInOut, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useEntityId from '@/entity/hooks/use-entity-id';
import useShowOnboarding from '../onboardings-shared/hooks/use-show-onboarding';
const PendingReviewBanner = () => {
  const $ = _c(27);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "pendingReviewBanner"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  const fpId = useEntityId();
  let t1;
  if ($[1] !== fpId) {
    t1 = getEntitiesByFpIdReviewsOptions({
      path: {
        fpId
      }
    });
    $[1] = fpId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !!fpId;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: reviews,
    isPending,
    error
  } = useQuery(t3);
  if (isPending || error || !reviews || reviews?.length === 0) {
    return null;
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: 0,
      opacity: 0
    };
    t5 = {
      height: "auto",
      opacity: 1
    };
    t6 = {
      height: 0,
      opacity: 0
    };
    t7 = {
      duration: 0.2,
      ease: easeInOut
    };
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IcoWarning16 color="error" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== reviews.length || $[12] !== t) {
    t9 = reviews.length === 1 ? t(_temp) : t(_temp2, {
      count: reviews.length
    });
    $[11] = reviews.length;
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <div className="flex items-center gap-1">{t8}<span className="text-label-2 text-error">{t9}</span></div>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== fpId || $[17] !== reviews) {
    let t12;
    if ($[19] !== fpId || $[20] !== reviews.length) {
      t12 = (review, index) => <div key={review.id} className="flex items-start gap-3 md:items-center">{reviews.length > 1 && <div className="flex items-center justify-center h-5 w-5 bg-secondary rounded-sm"><span className="text-caption-1 text-primary">{index + 1}</span></div>}<PendingReviewItem fpId={fpId} review={review} /></div>;
      $[19] = fpId;
      $[20] = reviews.length;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t11 = reviews.map(t12);
    $[16] = fpId;
    $[17] = reviews;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <div className="flex flex-col gap-2">{t11}</div>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t12) {
    t13 = <AnimatePresence mode="wait"><motion.div initial={t4} animate={t5} exit={t6} transition={t7} className="overflow-hidden"><div className="flex flex-col gap-4 p-4 border border-tertiary rounded-md mb-6">{t10}{t12}</div></motion.div></AnimatePresence>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
};
const PendingReviewItem = t0 => {
  const $ = _c(65);
  const {
    fpId,
    review
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "pendingReviewBanner"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const {
    scrollOnboardingIntoView
  } = useShowOnboarding();
  const {
    id,
    kind,
    assignedAgent,
    dueAt,
    onboardingId
  } = review;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = postEntitiesByFpIdActionsMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearReviewMutation = useMutation(t2);
  const confirm = useConfirmationDialog();
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  let t3;
  if ($[2] !== assignedAgent) {
    t3 = () => {
      if (!assignedAgent) {
        return "--";
      }
      if (assignedAgent.firstName) {
        return assignedAgent.lastName ? `${assignedAgent.firstName} ${assignedAgent.lastName}` : assignedAgent.firstName;
      }
      return assignedAgent.email;
    };
    $[2] = assignedAgent;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const formatAgentName = t3;
  let handleClearReviewClick;
  if ($[4] !== clearReviewMutation || $[5] !== confirm || $[6] !== fpId || $[7] !== id || $[8] !== queryClient || $[9] !== showRequestErrorToast || $[10] !== t || $[11] !== toast) {
    handleClearReviewClick = () => {
      confirm.open({
        description: t(_temp3),
        title: t(_temp4),
        primaryButton: {
          label: t(_temp5),
          onClick: async () => {
            await handleClearReview();
          }
        },
        secondaryButton: {
          label: t(_temp6)
        }
      });
    };
    const handleClearReview = async () => {
      ;
      try {
        await clearReviewMutation.mutateAsync({
          path: {
            fpId
          },
          body: {
            actions: [{
              kind: "clear_review",
              reviewIds: [id]
            }]
          }
        });
        await queryClient.invalidateQueries({
          queryKey: getEntitiesByFpIdReviewsQueryKey({
            path: {
              fpId
            }
          })
        });
        toast({
          title: t(_temp7),
          description: t(_temp8)
        });
      } catch (t4) {
        const error = t4;
        showRequestErrorToast(error);
      }
    };
    $[4] = clearReviewMutation;
    $[5] = confirm;
    $[6] = fpId;
    $[7] = id;
    $[8] = queryClient;
    $[9] = showRequestErrorToast;
    $[10] = t;
    $[11] = toast;
    $[12] = handleClearReviewClick;
  } else {
    handleClearReviewClick = $[12];
  }
  let t4;
  if ($[13] !== t) {
    t4 = t(_temp9);
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t4) {
    t5 = <span className="text-caption-2 text-tertiary pr-2">{t4}</span>;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== kind.name) {
    t6 = <span className="text-caption-1 text-primary break-words">{kind.name}</span>;
    $[17] = kind.name;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Divider className="hidden md:block rotate-90 w-4!" />;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t(_temp10);
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <span className="text-caption-2 text-tertiary pr-2">{t8}</span>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== formatAgentName) {
    t10 = formatAgentName();
    $[24] = formatAgentName;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <span className="text-caption-1 text-primary break-all md:break-normal">{t10}</span>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Divider className="hidden md:block rotate-90 w-4!" />;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t) {
    t13 = t(_temp11);
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13) {
    t14 = <span className="text-caption-2 text-tertiary pr-2">{t13}</span>;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== dueAt || $[34] !== formatDateWithTime) {
    t15 = formatDateWithTime(new Date(dueAt));
    $[33] = dueAt;
    $[34] = formatDateWithTime;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <span className="text-caption-1 text-primary">{t15}</span>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t11 || $[39] !== t14 || $[40] !== t16 || $[41] !== t5 || $[42] !== t6 || $[43] !== t9) {
    t17 = <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 items-center md:flex md:gap-2">{t5}{t6}{t7}{t9}{t11}{t12}{t14}{t16}</div>;
    $[38] = t11;
    $[39] = t14;
    $[40] = t16;
    $[41] = t5;
    $[42] = t6;
    $[43] = t9;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== onboardingId || $[46] !== scrollOnboardingIntoView) {
    t18 = () => scrollOnboardingIntoView(onboardingId);
    $[45] = onboardingId;
    $[46] = scrollOnboardingIntoView;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t) {
    t19 = t(_temp12);
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t18 || $[51] !== t19) {
    t20 = <LinkButton variant="caption-1" onClick={t18}>{t19}</LinkButton>;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="text-caption-1 text-tertiary">·</span>;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t) {
    t22 = t(_temp13);
    $[54] = t;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== handleClearReviewClick || $[57] !== t22) {
    t23 = <LinkButton variant="caption-1" onClick={handleClearReviewClick}>{t22}</LinkButton>;
    $[56] = handleClearReviewClick;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t20 || $[60] !== t23) {
    t24 = <div className="flex items-center gap-2">{t20}{t21}{t23}</div>;
    $[59] = t20;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t17 || $[63] !== t24) {
    t25 = <div className="flex flex-col items-start gap-2 min-w-0 md:min-w-auto md:flex-row md:items-center md:gap-5">{t17}{t24}</div>;
    $[62] = t17;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  return t25;
};
export default PendingReviewBanner;
function _temp($) {
  return $.title_one;
}
function _temp2($_0) {
  return $_0.title_other;
}
function _temp3($) {
  return $.confirmationDialog.description;
}
function _temp4($_0) {
  return $_0.confirmationDialog.title;
}
function _temp5($_1) {
  return $_1.confirmationDialog.yes;
}
function _temp6($_2) {
  return $_2.confirmationDialog.no;
}
function _temp7($_3) {
  return $_3.successToast.title;
}
function _temp8($_4) {
  return $_4.successToast.description;
}
function _temp9($_5) {
  return $_5.reviewKind;
}
function _temp10($_6) {
  return $_6.assignedTo;
}
function _temp11($_7) {
  return $_7.dueAt;
}
function _temp12($_8) {
  return $_8.viewOnboarding;
}
function _temp13($_9) {
  return $_9.clearReview;
}